import api from './api';
import type { SafetyReportCatalogResponse, SafetyReportRunResult } from '../types/safetySystem';

/**
 * Safety Report Center (V1 Safety "Reports" menu port, Phase 4).
 * Params are sent exactly as the form holds them (ISO date / datetime strings, option
 * values, raw text); blank values are dropped so the server applies its defaults.
 */

const BASE = '/safety-system/reports';

export function compactParams(params: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).trim() !== '') out[k] = v;
  }
  return out;
}

/** Content-Disposition may not be exposed through CORS; fall back to `<id>.xlsx`. */
export function filenameFromDisposition(disposition: unknown, fallback: string): string {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(String(disposition ?? ''));
  const name = match ? decodeURIComponent(match[1]).trim() : '';
  return name || fallback;
}

const safetyReportsService = {
  /** GET /safety-system/reports — catalog filtered server-side to what the caller may run. */
  async catalog(signal?: AbortSignal): Promise<SafetyReportCatalogResponse> {
    return (await api.get(BASE, { signal })).data as SafetyReportCatalogResponse;
  },

  /** GET /safety-system/reports/{id}?<params> — runs the report, records the run. */
  async run(id: string, params: Record<string, string>, signal?: AbortSignal): Promise<SafetyReportRunResult> {
    return (await api.get(`${BASE}/${encodeURIComponent(id)}`, { params: compactParams(params), signal })).data as SafetyReportRunResult;
  },

  /** GET /safety-system/reports/{id}/xlsx — the server-built workbook plus the name it chose. */
  async downloadXlsx(id: string, params: Record<string, string>): Promise<{ blob: Blob; filename: string }> {
    const response = await api.get(`${BASE}/${encodeURIComponent(id)}/xlsx`, { params: compactParams(params), responseType: 'blob' });
    return {
      blob: response.data as Blob,
      filename: filenameFromDisposition(response.headers?.['content-disposition'], `${id}.xlsx`),
    };
  },
};

export default safetyReportsService;
